import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Document, Page, Text, View, StyleSheet, PDFDownloadLink, pdf } from '@react-pdf/renderer';
import PDFLogo from '@/components/pdf/PDFLogo';
import { Header } from '../components/shared/Header/Header';
import LoadingState from '../components/shared/LoadingState/LoadingState';
import ErrorState from '../components/shared/LoadingState/ErrorState';
import { useState, useEffect } from 'react';

interface TemplateField {
  type: 'text' | 'date' | 'textarea';
  label: string;
  key: string;
}

interface TemplateGoal {
  title: string;
  fields: TemplateField[];
}

interface TemplateContent {
  title: string;
  fields: TemplateField[];
  instructions: string;
  goals: TemplateGoal[];
}

interface Template {
  id: string;
  name: string;
  goal: string;
  content: TemplateContent;
}

// PDF Styles
const pdfStyles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    padding: 40,
    paddingTop: 70,
    paddingBottom: 50,
    fontFamily: 'Helvetica',
  },
  headerBar: {
    position: 'absolute',
    top: 20,
    left: 40,
    right: 40,
    height: 30,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  logo: {
    width: 90,
    height: 22,
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 40,
    right: 40,
    textAlign: 'center',
    fontSize: 9,
    color: '#6b7280',
  },
  header: {
    marginBottom: 20,
  },
  title: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 10,
    textAlign: 'center',
  },
  headerFields: {
    marginBottom: 15,
  },
  field: {
    flexDirection: 'row',
    marginBottom: 6,
    alignItems: 'center',
  },
  fieldLabel: {
    fontSize: 10,
    fontWeight: 'bold',
    width: 100,
  },
  fieldLine: {
    flex: 1,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: '#000000',
    height: 16,
    marginLeft: 8,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginTop: 20,
    marginBottom: 10,
  },
  instructions: {
    fontSize: 10,
    lineHeight: 1.3,
    marginBottom: 15,
    textAlign: 'justify',
  },
  goalSection: {
    marginBottom: 15,
  },
  goalTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  textAreaField: {
    borderBottomWidth: 0,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#000000',
    minHeight: 40,
    marginLeft: 8,
  },
  multiLineField: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginBottom: 10,
  },
  multiLineLabel: {
    fontSize: 10,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  multiLineInput: {
    width: '100%',
    minHeight: 40,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#000000',
  },
});



const getBoardGovernanceFocuses = () => [
  'Board Membership',
  'Board Member Roles',
  'Board Communication',
  'Board Function',
  'Board Development'
];

const getProgramsServicesFocuses = () => [
  'Community Resource Mapping',
  'Curricula or Program Design',
  'Staff Knowledge & Skills',
  'Facilities',
  'Equipment & Supplies',
  'Intensity & Duration',
  'Goals & Outcomes',
  'Client Engagement & Feedback'
];

const getCommunityPartnershipFocuses = () => [
  'Mission Alignment',
  'Clear Roles & Expectations',
  'Formal Agreement',
  'Communication',
  'Share Resources',
  'Trust Development',
  'Partnership Evaluation & Adjustment'
];

const getResourceFunderDevelopmentFocuses = () => [
  'Fundraising Plan',
  'Funding Source Diversification',
  'Funder Relationship Development',
  'Grant Readiness',
  'Tracking & Reporting Impact',
  'Sustainability'
];

const getFinancialManagementFocuses = () => [
  'Budget Creation & Monitoring',
  'Financial Records',
  'Internal Controls',
  'Board Oversight',
  'Budget Forecasting',
  'Cash Management',
  'Cost Allocation'
];

const getDataSystemsTechnologyFocuses = () => [
  'Organizational Need-driven Tools',
  'Data Security & Confidentiality',
  'Data Management Strategy',
  'Staff Training',
  'Data Fidelity Oversight'
];

const getStaffManagementFocuses = () => [
  'Clear Job Descriptions',
  'Supportive Onboarding',
  'Professional Development',
  'Performance Reviews',
  'Open Communication',
  'Positive Organizational Culture',
  'Recognition & Incentives',
  'Staff Input & Engagement',
  'Retention Planning'
];

const getEvaluationFocuses = () => [
  'Intended Impact',
  'Mission Alignment',
  'Mixed Evaluation Methods',
  'Evaluation & Learning Plan',
  'Track Grant Requirements',
  'Review & Apply Evaluation Findings',
  'Engage Stakeholders'
];

const getDefaultFocuses = () => [
  'Purpose, Mission & Values',
  'Bylaws',
  'Incorporation',
  'IRS Tax Exemption Status',
  'Business/Regulatory Compliance',
  'Insurance & Risk Management',
  'Strategic Planning'
];

const getFocusesForTemplate = (templateName: string) => {
  switch (templateName) {
    case 'Board Governance':
      return getBoardGovernanceFocuses();
    case 'Programs & Services':
      return getProgramsServicesFocuses();
    case 'Community Partnership':
      return getCommunityPartnershipFocuses();
    case 'Resource & Funder Development':
      return getResourceFunderDevelopmentFocuses();
    case 'Financial Management':
      return getFinancialManagementFocuses();
    case 'Data Systems & Technology':
      return getDataSystemsTechnologyFocuses();
    case 'Staff Management':
      return getStaffManagementFocuses();
    case 'Evaluation':
      return getEvaluationFocuses();
    default:
      return getDefaultFocuses();
  }
};

const StatusCirclePDF: React.FC<{ label: string }> = ({ label }) => (
  <View style={{ flexDirection: 'row', alignItems: 'center', marginRight: 15 }}>
    <View style={{ width: 10, height: 10, borderRadius: 5, borderWidth: 1, borderStyle: 'solid', borderColor: '#000000', marginRight: 4 }} />
    <Text style={{ fontSize: 9 }}>{label}</Text>
  </View>
);

const TemplatePDFDocument: React.FC<{ template: Template }> = ({ template }) => {
  const templateFocuses = getFocusesForTemplate(template.name);

  return (
    <Document>
      {templateFocuses.map((focus) => (
        <Page key={focus} size="A4" style={pdfStyles.page}>
          <View style={pdfStyles.headerBar}>
            <PDFLogo width={90} height={22} />
          </View>
          
          <View style={{ alignItems: 'center', marginBottom: 15 }}>
            <Text style={pdfStyles.title}>CAPACITY BUILDING PLAN</Text>
            <Text style={{ fontSize: 12, marginBottom: 4 }}>{template.name}</Text>
            <Text style={{ fontSize: 10 }}>Focus: {focus}</Text>
          </View>

          <View style={{ borderWidth: 1, borderStyle: 'solid', borderColor: '#000000' }}>
            <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: '#000000' }}>
              <View style={{ width: '25%', padding: 8, borderRightWidth: 1, borderRightStyle: 'solid', borderRightColor: '#000000', backgroundColor: '#f3f4f6' }}>
                <Text style={{ fontWeight: 'bold', fontSize: 10 }}>Goal</Text>
              </View>
              <View style={{ flex: 1, padding: 8, minHeight: 40 }} />
            </View>

            <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: '#000000' }}>
              <View style={{ width: '25%', padding: 8, borderRightWidth: 1, borderRightStyle: 'solid', borderRightColor: '#000000' }}>
                <Text style={{ fontSize: 10 }}>Start Date:</Text>
                <Text style={{ marginTop: 6, fontSize: 10 }}>End Date:</Text>
              </View>
              <View style={{ flex: 1, padding: 8 }}>
                <Text style={{ marginBottom: 6, fontSize: 10 }}>Status (Circle):</Text>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                  <StatusCirclePDF label="Not Started" />
                  <StatusCirclePDF label="In Progress" />
                  <StatusCirclePDF label="On Hold" />
                  <StatusCirclePDF label="Completed" />
                </View>
              </View>
            </View>

            {['Action Step 1', 'Action Step 2', 'Action Step 3', 'Action Step 4', 'Action Step 5'].map((step, index) => (
              <View key={index} style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: '#000000' }}>
                <View style={{ width: '25%', padding: 8, borderRightWidth: 1, borderRightStyle: 'solid', borderRightColor: '#000000', backgroundColor: '#f3f4f6' }}>
                  <Text style={{ fontSize: 10 }}>{step}</Text>
                </View>
                <View style={{ flex: 1, padding: 8, minHeight: 30 }} />
              </View>
            ))}

            <View style={{ flexDirection: 'row' }}>
              <View style={{ width: '25%', padding: 8, borderRightWidth: 1, borderRightStyle: 'solid', borderRightColor: '#000000', backgroundColor: '#f3f4f6' }}>
                <Text style={{ fontSize: 10 }}>Notes/Updates</Text>
              </View>
              <View style={{ flex: 1, padding: 8, minHeight: 180 }} />
            </View>
          </View>

          <Text style={pdfStyles.footer}>Copyright @ 2025 ViaTelos. All Rights Reserved.</Text>
        </Page>
      ))}
    </Document>
  );
};

const TemplateField: React.FC<{ field: TemplateField }> = ({ field }) => {
  if (field.type === 'textarea') {
    return (
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          {field.label}
        </label>
        <div className="w-full h-20 border-2 border-gray-900 bg-white"></div>
      </div>
    );
  }

  return (
    <div className="flex items-center mb-4">
      <label className="text-sm font-semibold text-gray-900 w-48">
        {field.label}
      </label>
      <div className="flex-1 border-b-2 border-gray-900 h-6 ml-4"></div>
    </div>
  );
};

const TemplateGoal: React.FC<{ goal: TemplateGoal }> = ({ goal }) => (
  <div className="mb-8">
    <h3 className="text-base font-bold mb-4">{goal.title}</h3>
    {goal.fields.map((field, fieldIndex) => (
      <TemplateField key={fieldIndex} field={field} />
    ))}
  </div>
);

const TemplateHeader: React.FC<{ template: Template; onPrint: () => void; t: (key: string) => string }> = ({ 
  template, 
  onPrint, 
  t 
}) => (
  <Header 
    backUrl="/templates"
    rightContent={
      <>
        <PDFDownloadLink
          document={<TemplatePDFDocument template={template} />}
          fileName={`${template.name.toLowerCase().replace(/\s+/g, '-')}-template.pdf`}
          className="bg-gray-800 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors mr-2"
        >
          {({ loading: pdfLoading }: { loading: boolean }) => (
            pdfLoading ? 
              t('templates.generating') : 
              t('templates.downloadPDF')
          )}
        </PDFDownloadLink>

        <button
          onClick={onPrint}
          className="bg-[#5E9E2E] text-white px-4 py-2 rounded-lg hover:bg-[#4c8025] transition-colors"
          type="button"
        >
          {t('templates.printPDF')}
        </button>
      </>
    }
  />
);

const StatusCircle: React.FC<{ label: string }> = ({ label }) => (
  <span className="inline-flex items-center mr-4">
    <span className="w-2.5 h-2.5 border border-gray-900 rounded-full mr-1"></span>
    <span className="text-xs uppercase">{label}</span>
  </span>
);

const TemplateContent: React.FC<{ template: Template }> = ({ template }) => {
  const templateFocuses = getFocusesForTemplate(template.name);

  return (
    <div className="mt-[20px]">
      {templateFocuses.map((focus, index) => (
        <div key={focus} className={`bg-white rounded-lg shadow-lg p-8 ${index > 0 ? 'mt-8' : ''}`}>
          <div className="text-center mb-4">
            <h1 className="text-base font-bold uppercase mb-1">
              CAPACITY BUILDING PLAN
            </h1>
            <div className="text-sm uppercase">
              {template.name}
            </div>
            <div className="text-xs">
              Focus: {focus}
            </div>
          </div>

          <div className="border border-gray-900">
            <div className="flex">
              <div className="w-1/4 p-2 border-r border-b border-gray-900 bg-gray-100">
                <div className="text-xs font-bold uppercase">Goal</div>
              </div>
              <div className="flex-1 p-2 border-b border-gray-900 min-h-[40px]"></div>
            </div>

            <div className="flex">
              <div className="w-1/4 p-2 border-r border-b border-gray-900">
                <div className="text-xs">Start Date:</div>
                <div className="mt-1.5 text-xs">End Date:</div>
              </div>
              <div className="flex-1 p-2 border-b border-gray-900">
                <div className="text-xs mb-1.5">Status (Circle):</div>
                <div className="flex flex-wrap">
                  <StatusCircle label="Not Started" />
                  <StatusCircle label="In Progress" />
                  <StatusCircle label="On Hold" />
                  <StatusCircle label="Completed" />
                </div>
              </div>
            </div>

            {['Action Step 1', 'Action Step 2', 'Action Step 3', 'Action Step 4', 'Action Step 5'].map((step, index) => (
              <div key={index} className="flex">
                <div className="w-1/4 p-2 border-r border-b border-gray-900 bg-gray-100">
                  <div className="text-xs">{step}</div>
                </div>
                <div className="flex-1 p-2 border-b border-gray-900 min-h-[30px]"></div>
              </div>
            ))}

            <div className="flex">
              <div className="w-1/4 p-2 border-r border-gray-900 bg-gray-100">
                <div className="text-xs">Notes/Updates</div>
              </div>
              <div className="flex-1 p-2 min-h-[180px]"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const TemplateViewer: React.FC = () => {
  const { id: templateId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [template, setTemplate] = useState<Template | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!templateId) {
      setLoading(false);
      return;
    }

    // Import templates data
    import('@/constants/templates').then(({ TEMPLATES }) => {
      const foundTemplate = TEMPLATES[templateId as keyof typeof TEMPLATES];
      if (foundTemplate) {
        setTemplate({
          id: foundTemplate.id,
          name: foundTemplate.name,
          goal: foundTemplate.goal,
          content: {
            ...foundTemplate.content,
            fields: foundTemplate.content.fields.map(field => ({
              ...field,
              type: field.type as 'text' | 'date' | 'textarea'
            })),
            goals: foundTemplate.content.goals.map(goal => ({
              ...goal,
              fields: goal.fields.map(field => ({
                ...field,
                type: field.type as 'text' | 'date' | 'textarea'
              }))
            }))
          }
        });
      } else {
        setTemplate(null);
      }
      setLoading(false);
    });
  }, [templateId]);

  const handlePrint = async (): Promise<void> => {
    if (!template) return;
    
    try {
      const pdfDoc = <TemplatePDFDocument template={template} />;
      const asPdf = pdf(pdfDoc);
      const blob = await asPdf.toBlob();
      const url = URL.createObjectURL(blob);
      
      const printWindow = window.open(url);
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          printWindow.print();
        });
      }
    } catch (error) {
      console.error('Error generating PDF for print:', error);
    }
  };

  const handleGoBack = () => {
    navigate('/templates');
  };

  if (loading) {
    return <LoadingState />;
  }

  if (!template) {
    return (
      <ErrorState 
        message={t('templates.templateNotFound')}
        buttonText={t('common.goBack')}
        onButtonClick={handleGoBack}
      />
    );
  }

  return (
    <div>
      <TemplateHeader template={template} onPrint={handlePrint} t={t} />
      <TemplateContent template={template} />
    </div>
  );
};

export default TemplateViewer;